import { httpsCallable } from 'firebase/functions'
import { signInWithCustomToken } from 'firebase/auth'
import { getOrCreateInstallId } from './deviceStorage'
import { getFirebaseAuth, getFirebaseFunctions } from './firebase'
import {
  clearPosResumeAccount,
  readPosResumeAccount,
  shouldClearSavedResume,
  writePosResumeAccount,
  type PosResumeAccount,
} from './posResumeLocal'

export const RESUME_WAITING_MESSAGE = 'Waiting for a connection. We’ll sign you in when it’s back.'
export const RESUME_CLEARED_MESSAGE = 'This tablet needs your email and password again.'

interface IssuePosResumeResponse {
  token: string
  uid: string
  name: string
  email: string
}

interface ResumePosSessionResponse extends IssuePosResumeResponse {
  customToken: string
}

function functionsErrorCode(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) return String((err as { code: unknown }).code)
  return ''
}

function requireFunctions() {
  const functions = getFirebaseFunctions()
  if (!functions) throw new Error('Cloud Functions are not configured.')
  return functions
}

/** Ask the server for a fresh device token after email/password sign-in. */
export async function issuePosResume(installId: string, issuedAt: number): Promise<PosResumeAccount> {
  const callable = httpsCallable<{ installId: string; issuedAt: number }, IssuePosResumeResponse>(
    requireFunctions(),
    'issuePosResume',
  )
  const result = await callable({ installId, issuedAt })
  return result.data
}

export async function revokePosResume(installId: string): Promise<void> {
  const callable = httpsCallable<{ installId: string }, { ok: true }>(
    requireFunctions(),
    'revokePosResume',
  )
  await callable({ installId })
}

/**
 * Exchange the saved device token for a Firebase session.
 * A rejected token clears the saved account. A network failure leaves it in place.
 */
export async function continueAsSavedAccount(): Promise<void> {
  const saved = readPosResumeAccount()
  if (!saved) {
    const cleared = new Error(RESUME_CLEARED_MESSAGE)
    throw cleared
  }
  try {
    const callable = httpsCallable<{ installId: string; token: string; uid: string }, ResumePosSessionResponse>(
      requireFunctions(),
      'resumePosSession',
    )
    const result = await callable({
      installId: getOrCreateInstallId(),
      token: saved.token,
      uid: saved.uid,
    })
    const next: PosResumeAccount = {
      uid: result.data.uid,
      name: result.data.name,
      email: result.data.email,
      token: result.data.token,
    }
    writePosResumeAccount(next)
    const auth = getFirebaseAuth()
    if (!auth) throw new Error('Firebase Auth is not configured')
    await signInWithCustomToken(auth, result.data.customToken)
  } catch (err) {
    if (err instanceof Error && err.message === RESUME_CLEARED_MESSAGE) throw err
    if (shouldClearSavedResume(functionsErrorCode(err))) {
      clearPosResumeAccount()
      throw new Error(RESUME_CLEARED_MESSAGE)
    }
    throw new Error(RESUME_WAITING_MESSAGE)
  }
}
